import { PayloadAction, createSelector, createSlice } from "@reduxjs/toolkit";

import type { RootState } from "app/store";
import setupLocalStorageAPI from "utils/setupLocalStorageAPI";

export type ActionType = "add" | "update" | "delete" | "cancel";

const showRoomTrafficEditingToolsStorage = setupLocalStorageAPI(
  "showRoomTrafficEditingTools"
);

const showRoomTrafficAddPlaceholderStorage = setupLocalStorageAPI(
  "showRoomTrafficAddPlaceholder"
);

interface EditRoomSettings {
  roomId?: number;
  roomName?: string;
  roomType?: number;
  open?: boolean;
}

interface RoomTrafficState {
  autoTriggerShowWelcomeDialog: boolean;
  showWelcomeDialog: boolean;
  isRoomsManagementEnabled: boolean;
  showNewRoomPlaceholder?: boolean;
  editRoomSettings: EditRoomSettings;
}

const initialState: RoomTrafficState = {
  autoTriggerShowWelcomeDialog: true,
  showWelcomeDialog: false,
  isRoomsManagementEnabled: showRoomTrafficEditingToolsStorage.getData(false),
  showNewRoomPlaceholder: showRoomTrafficAddPlaceholderStorage.getData(true),
  editRoomSettings: {
    roomId: undefined,
    roomName: undefined,
    roomType: undefined,
    open: false
  }
};

export const roomTrafficSlice = createSlice({
  name: "room-traffic",
  initialState,
  reducers: {
    setAutoTriggerShowWelcomeDialog: (
      state,
      action: PayloadAction<boolean>
    ) => {
      state.autoTriggerShowWelcomeDialog = action.payload;
    },
    setToggleShowWelcomeDialog: (
      state,
      action: PayloadAction<boolean | undefined>
    ) => {
      if (action.payload === undefined) {
        state.showWelcomeDialog = !state.showWelcomeDialog;
      } else {
        state.showWelcomeDialog = action.payload;
      }
    },
    setIsRoomsManagementEnabled: (state, action: PayloadAction<boolean>) => {
      state.isRoomsManagementEnabled = action.payload;
      showRoomTrafficEditingToolsStorage.updateData(
        state.isRoomsManagementEnabled
      );

      if (state.isRoomsManagementEnabled) {
        state.showNewRoomPlaceholder = true;
        showRoomTrafficAddPlaceholderStorage.updateData(true);
      }
    },
    setEditRoomSettings: (state, action: PayloadAction<EditRoomSettings>) => {
      state.editRoomSettings = action.payload;
    },
    setShowAddRoomPlaceHolder: (state, action: PayloadAction<boolean>) => {
      state.showNewRoomPlaceholder = action.payload;
      showRoomTrafficAddPlaceholderStorage.updateData(action.payload);
    }
  }
});

export const {
  setToggleShowWelcomeDialog,
  setAutoTriggerShowWelcomeDialog,
  setIsRoomsManagementEnabled,
  setEditRoomSettings,
  setShowAddRoomPlaceHolder
} = roomTrafficSlice.actions;

const roomTrafficSelector = (state: RootState) => state.roomTraffic;
export const selectShowWelcomeDialog = createSelector(
  roomTrafficSelector,
  (state) => state.showWelcomeDialog
);

export const selectAutoTriggerShowWelcomeDialog = createSelector(
  roomTrafficSelector,
  (state) => state.autoTriggerShowWelcomeDialog
);

export const selectIsRoomsManagementEnabled = createSelector(
  roomTrafficSelector,
  (state) => state.isRoomsManagementEnabled
);

export const selectEditRoomSettings = createSelector(
  roomTrafficSelector,
  (state) => state.editRoomSettings
);

export const selectShowAddRoomPlaceHolder = createSelector(
  roomTrafficSelector,
  (state) => state.showNewRoomPlaceholder
);

export default roomTrafficSlice.reducer;
